import { DataPoint } from "./MarketDataGenerator";

// In-memory cache to throttle and deduplicate calls on client side
const clientFetchCache = new Map<string, { data: any[]; timestamp: number }>();
const inFlightRequests = new Map<string, Promise<any[] | null>>();
const CACHE_TTL_MS = 2000; // 2 seconds client debounce
/**
 * F3a: grava no cache e apaga as entradas vencidas. Antes nada saía do cache: com a navegação sem teto (janela
 * deslizante) cada bloco pedido ficava na memória para sempre. A regra de 2 s de reaproveitamento não muda.
 */
function cacheSet(key: string, data: any[]): void {
  const now = Date.now();
  for (const [k, v] of clientFetchCache) if (now - v.timestamp >= CACHE_TTL_MS) clientFetchCache.delete(k);
  clientFetchCache.set(key, { data, timestamp: now });
}

// F1 (CandleSource): de qual servidor veio cada resposta (cabeçalho X-Candle-Source do proxy). Só leitura; não muda a busca.
const responseSource = new WeakMap<object, string>();
/** Servidor(es) que responderam este array de velas (null = desconhecido, ex.: Yahoo ou servidor antigo). */
export function candleSourceOf(rows: object): string | null { return responseSource.get(rows) ?? null; }
/** Anota status HTTP e status dos servidores de origem no erro (a mensagem não muda). */
function httpError(message: string, response: Response): Error {
  const err: any = new Error(message);
  err.status = response.status;
  err.upstream = response.headers.get('X-Upstream-Status');
  return err;
}


export async function fetchDbData(symbol: string, limit: number = 600): Promise<any[] | null> {
  const url = `/api/db/candles?symbol=${encodeURIComponent(symbol)}&limit=${limit}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`DB API Error: ${response.status}`);
  }
  const data = await response.json();
  
  // Convert our DB format back to the format BinanceWorker expects:
  // [openTime, open, high, low, close, volume, closeTime]
  return data.map((d: any) => [
    new Date(d.time).getTime(),
    d.open,
    d.high,
    d.low,
    d.close,
    d.volume,
    new Date(d.time).getTime() + 60000 // assuming 1m for now
  ]);
}

export async function fetchRawBinanceData(symbol: string, interval: string, limit: number = 600, endTime?: number): Promise<any[] | null> {
  const reqKey = `${symbol.toUpperCase()}_${interval}_${limit}_${endTime || 'live'}`;
  
  // 1. Check in-memory client cache
  const cached = clientFetchCache.get(reqKey);
  if (cached && (Date.now() - cached.timestamp < CACHE_TTL_MS)) {
    return cached.data;
  }

  // 2. Check if identical request is already in-flight (deduplication)
  if (inFlightRequests.has(reqKey)) {
    return inFlightRequests.get(reqKey)!;
  }

  const fetchPromise = (async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000); 

    try {
      // Intercept Traditional Markets for Yahoo Finance via local proxy
      const traditionalMarkets = ['US100', 'US500', 'AAPL', 'TSLA', 'NVDA', 'EURUSD'];
      if (traditionalMarkets.includes(symbol.toUpperCase())) {
         let ySymbol = symbol.toUpperCase();
         if (ySymbol === 'US100') ySymbol = '^NDX';
         if (ySymbol === 'US500') ySymbol = '^GSPC';
         if (ySymbol === 'EURUSD') ySymbol = 'EURUSD=X';

         const intervalMap: Record<string, string> = {
            '1m': '1m', '3m': '2m', '5m': '5m', '15m': '15m', '20m': '15m', '30m': '30m', '1h': '1h', '2h': '1h', '4h': '1h', '6h': '1h', '8h': '1h', '12h': '1h', '1d': '1d', '3d': '1d', '1w': '1wk', '1M': '1mo'
         };
         const rangeMap: Record<string, string> = {
            '1m': '1d', '3m': '1d', '5m': '5d', '15m': '5d', '20m': '5d', '30m': '1mo', '1h': '1mo', '2h': '1mo', '4h': '3mo', '6h': '3mo', '8h': '3mo', '12h': '3mo', '1d': '1y', '3d': '1y', '1w': '5y', '1M': 'max'
         };
         const yInt = intervalMap[interval] || '1d';
         const yRange = rangeMap[interval] || '1mo';
         
         const url = `/api/yahoo?symbol=${encodeURIComponent(ySymbol)}&interval=${yInt}&range=${yRange}`;
         
         const response = await fetch(url, { signal: controller.signal, cache: 'no-store' });
         clearTimeout(timeoutId);
         
         if (!response.ok) {
             throw httpError(`Yahoo API Unreachable. Last status: ${response.status}`, response);
         }
         
         const json = await response.json();
         const result = json.chart?.result?.[0];
         if (!result || !result.timestamp) throw new Error("No data returned from Yahoo");
         
         const timestamps = result.timestamp;
         const { open, high, low, close, volume } = result.indicators.quote[0];
         
         const formattedData = [];
         for (let i = 0; i < timestamps.length; i++) {
            if (!close[i] || !open[i] || !high[i] || !low[i]) continue;
            
            const openTime = timestamps[i] * 1000;
            let closeTime = openTime + 60000;
            if (interval === '3m') closeTime = openTime + 180000;
            if (interval === '5m') closeTime = openTime + 300000;
            if (interval === '15m') closeTime = openTime + 900000;
            if (interval === '20m') closeTime = openTime + 1200000;
            if (interval === '30m') closeTime = openTime + 1800000;
            if (interval === '1h') closeTime = openTime + 3600000;
            if (interval === '2h') closeTime = openTime + 7200000;
            if (interval === '4h') closeTime = openTime + 14400000;
            if (interval === '6h') closeTime = openTime + 21600000;
            if (interval === '8h') closeTime = openTime + 28800000;
            if (interval === '12h') closeTime = openTime + 43200000;
            if (interval === '1d') closeTime = openTime + 86400000;
            if (interval === '3d') closeTime = openTime + 259200000;
            if (interval === '1w') closeTime = openTime + 604800000;
            if (interval === '1M') closeTime = openTime + 2592000000;

            formattedData.push([
               openTime, 
               open[i].toString(), 
               high[i].toString(), 
               low[i].toString(), 
               close[i].toString(), 
               (volume[i] || 0).toString(),
               closeTime
            ]);
         }
         const finalData = formattedData.slice(-limit);
         cacheSet(reqKey, finalData);
         return finalData;
      }

      // Call local binance proxy
      let apiSymbol = symbol;
      if (apiSymbol.toUpperCase() === 'XAUUSDT' || apiSymbol.toUpperCase() === 'XAUUSD') apiSymbol = 'PAXGUSDT';
      
      const url = endTime 
        ? `/api/binance?symbol=${encodeURIComponent(apiSymbol)}&interval=${interval}&limit=${limit}&endTime=${endTime}`
        : `/api/binance?symbol=${encodeURIComponent(apiSymbol)}&interval=${interval}&limit=${limit}`;
      const tNetP1 = p1T();
      const response = await fetch(url, { signal: controller.signal, cache: 'no-store' });
      if (endTime) p1End(P1S.NET, tNetP1); // P1 (?dbg=1): etapa "rede" da carga de histórico
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
          if (response.status === 400) {
              throw httpError(`Ticker "${symbol}" não encontrado. Se for cripto, use o par completo (ex: BTCUSDT).`, response);
          }
          throw httpError(`Ocorreu um erro de rede (${response.status}). Tente outro ativo.`, response);
      }
      
      const tJsonP1 = p1T();
      const resJson = await response.json();
      if (endTime) p1End(P1S.JSON, tJsonP1); // P1: leitura do corpo + análise do JSON
      if (Array.isArray(resJson)) {
        const src = response.headers.get('X-Candle-Source');
        if (src) responseSource.set(resJson, src);
        cacheSet(reqKey, resJson);
      }
      return resJson;
    } catch (error) {
      throw error;
    } finally {
      inFlightRequests.delete(reqKey);
    }
  })();

  inFlightRequests.set(reqKey, fetchPromise);
  return fetchPromise;
}

export async function fetchBinanceData(symbol: string, interval: string, limit: number = 200, endTime?: number, options?: { sma10Period?: number, sma20Period?: number, sma50Period?: number }): Promise<DataPoint[] | null> {
  const rawData = await fetchRawBinanceData(symbol, interval, limit, endTime);
  if (!rawData) return null;
  return await processRawBinanceData(rawData, options);
}

import { processMathData, normalizeBinanceToCandle } from './math-processor';
import { DBG_ENABLED, dbgMathCall } from './debugTelemetry';
import { p1T, p1End, S as P1S } from './p1Probe';
import { jank } from './jankRecorder';

export async function processRawBinanceData(rawData: any[], options?: { sma10Period?: number, sma20Period?: number, sma50Period?: number }): Promise<DataPoint[]> {
    if (!rawData || rawData.length === 0) return [];
    try {
        const tNormP1 = p1T();
        const candles = normalizeBinanceToCandle(rawData);
        if (DBG_ENABLED && jank?.inCargaMath) p1End(P1S.NORM, tNormP1); // P1: normalização (só de carga)
        // Direct execution on main thread avoids Structured Clone Algorithm memory limits (DataCloneError)
        if (DBG_ENABLED) {
            // Telemetria passiva (?dbg=1): só mede o tempo; não altera nada
            const tDbg = performance.now();
            const result = processMathData(candles, options);
            dbgMathCall(performance.now() - tDbg, candles.length);
            return result;
        }
        return processMathData(candles, options);
    } catch (e) {
        console.error("Error processing data:", e);
        return [];
    }
}
